import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BackgroundIcons from '../components/BackgroundIcons';
import NotificationModal from '../components/NotificationModal';

// VowAI Homepage - Updated with signature button styling - Nov 20, 2024
// Button fixes: View Dashboard white bg, restored original sizes, Gemini logo fixed
const Home = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'success'
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Handle View Dashboard click with authentication validation
  const handleDashboardClick = (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    if (!token) {
      // Show magical VowAI-style notification if not logged in
      setNotification({
        show: true,
        title: 'Sign In Required',
        message: '✨ Please sign in to access your magical wedding dashboard! Your dreams await... 💖',
        type: 'warning',
        actionLabel: 'Log In',
        onAction: () => window.dispatchEvent(new Event('openAuthModal'))
      });
    } else {
      // Navigate to dashboard if logged in
      navigate('/dashboard');
    }
  };

  const features = [
    {
      title: "Wedding Blueprint",
      icon: "💎",
      description: "Design your perfect wedding with AI-powered planning tools",
      gradient: "from-rose-100 to-pink-200",
      borderColor: "border-rose-200",
      subFeatures: [
        { name: "Dream Wedding Generator", path: "/wedding-blueprint/dream-wedding-generator", icon: "✨" },
        { name: "Moodboard Matcher", path: "/wedding-blueprint/moodboard-matcher", icon: "🎨" },
        { name: "Budget Allocator", path: "/wedding-blueprint/budget-allocator", icon: "💰" }
      ]
    },
    {
      title: "Vendor Vault",
      icon: "🏪",
      description: "Discover and connect with the perfect wedding vendors",
      gradient: "from-purple-100 to-lavender-200",
      borderColor: "border-purple-200",
      subFeatures: [
        { name: "Smart Vendor Matching", path: "/vendor-vault/smart-vendor-matching", icon: "🔍" },
        { name: "Vendor Shortlisting Assistant", path: "/vendor-vault/vendor-shortlisting-assistant", icon: "📋" }
      ]
    },
    {
      title: "WedCraft Studio",
      icon: "🎭",
      description: "Create beautiful wedding materials and explore traditions",
      gradient: "from-gold-100 to-yellow-200",
      borderColor: "border-gold-200",
      subFeatures: [
        { name: "Invite Generator", path: "/wedcraft-studio/invite-generator", icon: "💌" },
        { name: "Cultural Ritual Explainer", path: "/wedcraft-studio/cultural-ritual-explainer", icon: "📖" }
      ]
    },
    {
      title: "The Big Day Playbook",
      icon: "📅",
      description: "Organize every detail for your perfect wedding day",
      gradient: "from-emerald-100 to-mint-200",
      borderColor: "border-emerald-200",
      subFeatures: [
        { name: "Timeline Generator", path: "/big-day-playbook/timeline-generator", icon: "⏰" },
        { name: "Weather Risk Advisor", path: "/big-day-playbook/weather-risk-advisor", icon: "🌤️" }
      ]
    },
    {
      title: "Ever After Memories",
      icon: "💕",
      description: "Preserve and cherish your wedding memories forever",
      gradient: "from-pink-100 to-rose-200",
      borderColor: "border-pink-200",
      subFeatures: [
        { name: "Thank You Note Generator", path: "/ever-after-memories/thank-you-note-generator", icon: "💌" },
        { name: "Memory Capsule Creator", path: "/ever-after-memories/memory-capsule-creator", icon: "📦" }
      ]
    }
  ];

  const homeIcons = [
    { emoji: '💖', size: '4xl', opacity: '15' },
    { emoji: '💍', size: '4xl', opacity: '15' },
    { emoji: '✨', size: '4xl', opacity: '15' },
    { emoji: '🥂', size: '4xl', opacity: '15' },
    { emoji: '💎', size: '4xl', opacity: '15' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={homeIcons} />
      
      {/* Floating sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-10"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          >
            <span className="text-gold-400 text-lg">✨</span>
          </div>
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative z-10 pt-20 pb-32">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* VowAI Logo/Icon */}
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
              <span className="text-4xl animate-pulse">💍</span>
            </div>

            {/* Main Title */}
            <h1 className="text-7xl md:text-8xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide">
              VowAI
            </h1>

            {/* Elegant underline */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="w-40 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-gold-400">💕</div>
              </div>
            </div>

            {/* Tagline */}
            <p className="text-2xl md:text-3xl text-gray-700 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
              Your AI-powered wedding planning companion that transforms dreams into reality with intelligent insights and personalized guidance
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link
                to="/wedding-blueprint/dream-wedding-generator"
                className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white px-12 py-4 rounded-2xl font-bold text-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
              >
                <span className="mr-3 text-2xl animate-bounce">✨</span>
                Start Planning
                <span className="ml-3 text-2xl animate-bounce">✨</span>
                {/* VowAI signature glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              </Link>

              <button
                onClick={handleDashboardClick}
                className="group relative overflow-hidden bg-white/80 backdrop-blur-sm text-rose-600 px-12 py-4 rounded-2xl font-bold text-xl border-2 border-rose-200 hover:bg-rose-50 hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                <span className="mr-3 text-2xl animate-bounce">💖</span>
                View Dashboard
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-6xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent mb-6">
                Your Wedding Journey
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                From dream to reality, our AI-powered tools guide you through every step of planning your perfect wedding
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={`group bg-gradient-to-br ${feature.gradient} backdrop-blur-lg rounded-3xl shadow-2xl border-2 ${feature.borderColor} p-8 hover:shadow-3xl hover:scale-105 transition-all duration-500 animate-fadeInScale delay-${index * 200}`}
                >
                  {/* Feature Icon */}
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-white/50 rounded-full shadow-lg mb-4 group-hover:animate-bounce">
                      <span className="text-4xl">{feature.icon}</span>
                    </div>
                    <h3 className="text-2xl font-serif font-bold text-gray-800 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed font-light">
                      {feature.description}
                    </p>
                  </div>

                  {/* Sub-features */}
                  <div className="space-y-3">
                    {feature.subFeatures.map((subFeature, subIndex) => (
                      <Link
                        key={subFeature.name}
                        to={subFeature.path}
                        className="group/sub flex items-center p-4 bg-gradient-to-r from-white/60 to-rose-50/60 rounded-xl border border-white/50 hover:shadow-lg hover:scale-105 transition-all duration-300"
                      >
                        <span className="text-2xl mr-3 group-hover/sub:animate-bounce">
                          {subFeature.icon}
                        </span>
                        <span className="font-semibold text-gray-700 group-hover/sub:text-rose-600 transition-colors duration-300">
                          {subFeature.name}
                        </span>
                        <span className="ml-auto text-rose-400 group-hover/sub:translate-x-1 transition-transform duration-300">
                          →
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VowBot Introduction */}
      <section className="relative z-10 pt-20 pb-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-gradient-to-r from-purple-400/20 via-pink-400/20 to-rose-400/20 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-white/50 p-12 text-center">
              
              {/* VowBot Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full shadow-2xl mb-8 animate-bounce">
                <span className="text-3xl animate-pulse">🤖</span>
              </div>

              {/* VowBot Title */}
              <h2 className="text-4xl md:text-5xl font-serif font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
                Meet VowBot
              </h2>

              {/* Elegant underline */}
              <div className="flex justify-center mb-8">
                <div className="relative">
                  <div className="w-32 h-1 bg-gradient-to-r from-transparent via-purple-400 to-transparent rounded-full"></div>
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-purple-400">💜</div>
                </div>
              </div>

              {/* VowBot Description */}
              <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto leading-relaxed">
                Your personal AI wedding planning assistant is here to help 24/7. Ask questions, get recommendations, and receive personalized guidance throughout your wedding journey.
              </p>

              {/* VowBot Features */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white/30 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="text-3xl mb-3">🧠</div>
                  <h4 className="font-bold text-gray-800 mb-2">Smart Recommendations</h4>
                  <p className="text-gray-600 text-sm">Get personalized vendor and planning suggestions</p>
                </div>
                <div className="bg-white/30 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="text-3xl mb-3">💬</div>
                  <h4 className="font-bold text-gray-800 mb-2">24/7 Support</h4>
                  <p className="text-gray-600 text-sm">Ask questions anytime, get instant helpful responses</p>
                </div>
                <div className="bg-white/30 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="text-3xl mb-3">🎯</div>
                  <h4 className="font-bold text-gray-800 mb-2">Personalized Guidance</h4>
                  <p className="text-gray-600 text-sm">Tailored advice based on your preferences and budget</p>
                </div>
              </div>

              {/* VowBot CTA */}
              <p className="text-lg text-gray-600 mb-6">
                Look for the chat widget in the bottom right corner to start chatting with VowBot!
              </p>

              {/* Success Stories */}
              <div className="bg-gradient-to-r from-rose-50/50 to-pink-50/50 rounded-2xl p-8 backdrop-blur-sm">
                <h3 className="text-2xl font-serif font-bold text-gray-800 mb-4">
                  Join thousands of couples who have transformed their wedding dreams into reality with VowAI
                </h3>
                <p className="text-gray-600 mb-6">
                  Start planning today and discover how AI can make your special day absolutely perfect.
                </p>

                {/* Final CTA Button */}
                <Link
                  to="/wedding-blueprint/dream-wedding-generator"
                  className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white px-16 py-6 rounded-2xl font-bold text-2xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
                >
                  <span className="mr-4 text-3xl animate-bounce">💖</span>
                  Start Planning Your Dream Wedding
                  <span className="ml-4 text-3xl animate-bounce">💖</span>
                  {/* VowAI signature glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                </Link>

                {/* Trust Indicators */}
                <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-gray-500">
                  <div className="flex items-center">
                    <span className="text-2xl mr-2 animate-bounce">🤖</span>
                    <span className="font-medium">AI-Powered</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-2xl mr-2 animate-bounce">🔒</span>
                    <span className="font-medium">Secure & Private</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-2xl mr-2 animate-bounce">💕</span>
                    <span className="font-medium">Made with Love</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Footer */}
      <footer className="relative z-10 pt-4 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          {/* Divider with emoji */}
          <div className="flex items-center justify-center mb-10">
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            <div className="mx-4 text-3xl animate-bounce">✨</div>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
          </div>

          {/* Glassmorphic Card */}
          <div className="group bg-white/80 backdrop-blur-lg rounded-3xl p-10 shadow-2xl border-2 border-rose-200/50 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500 text-center">
            
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-6 animate-bounce">
              <span className="text-3xl animate-pulse">🚀</span>
            </div>

            <p className="text-base text-gray-500 uppercase tracking-widest mb-3 font-medium">Built & Engineered by</p>
            <h3 className="text-4xl md:text-5xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-2 tracking-wide">
              Pranav Raut
            </h3>
            <p className="text-xl text-gray-600 font-light italic mb-6">AI Engineer & Founder</p>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
              VowAI began as a hands-on exploration of production AI systems and evolved into a platform powered by multi-model orchestration, intelligent agents, RAG pipelines, evaluation frameworks, and real-world user feedback loops.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <a
                href="https://www.linkedin.com/in/pranavkraut/"
                target="_blank"
                rel="noopener noreferrer"
                className="relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white px-8 py-3 rounded-2xl font-bold hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse inline-flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                Connect on LinkedIn
                <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              </a>
              <a
                href="/admin/vowai-labs"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm text-rose-600 px-8 py-3 rounded-2xl font-bold border-2 border-rose-200/50 hover:border-rose-400 hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                <span className="animate-bounce inline-block">🧪</span> Inside VowAI
                <span className="ml-1">→</span>
              </a>
            </div>

            <p className="text-gray-400 text-xs font-medium">© 2026 VowAI. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fadeInScale {
          animation: fadeInScale 0.8s ease-out;
        }
        .delay-200 {
          animation-delay: 200ms;
        }
        .delay-400 {
          animation-delay: 400ms;
        }
        .delay-600 {
          animation-delay: 600ms;
        }
        .delay-800 {
          animation-delay: 800ms;
        }
      `}</style>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        title={notification.title}
        message={notification.message}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        onClose={() => setNotification({ show: false, title: '', message: '', type: 'success' })}
      />
    </div>
  );
};

export default Home;
